import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styles from './Search.module.css';
import searchIcon from '../../assets/pic/search.svg';
import backIcon from '../../assets/pic/arrow2.svg';
import resolveMediaUrl from '../../utils/resolveMediaUrl';
import {
    searchStores,
    getFavorites,
    addFavorite,
    deleteFavorite
} from '../../apis/storeSearchApi';

const Search = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const keywordParam = searchParams.get('keyword') || '';

    // UI State
    const [keyword, setKeyword] = useState(keywordParam);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Favorites State: Map<UniqueKey, FavoriteID>
    // UniqueKey logic: item.link || (item.storeName + item.address)
    const [favoriteIds, setFavoriteIds] = useState(new Map());

    useEffect(() => {
        setKeyword(keywordParam);
    }, [keywordParam]);

    // Initial Load: Favorites
    useEffect(() => {
        fetchFavorites();
    }, []);

    const fetchFavorites = async () => {
        try {
            const favs = await getFavorites();
            // favs is Array of object. We need to map them to find them by content.
            // Assumption: Store items have same fields as Favorite items.
            const newMap = new Map();
            if (Array.isArray(favs)) {
                favs.forEach(fav => {
                    const key = fav.link || `${fav.storeName}-${fav.address}`;
                    if (fav.id || fav.favoriteId) {
                        newMap.set(key, fav.id || fav.favoriteId);
                    }
                });
            }
            setFavoriteIds(newMap);
        } catch (err) {
            console.error("Failed to load favorites", err);
        }
    };

    // Search Logic
    useEffect(() => {
        const term = keywordParam.trim();
        if (!term) {
            setResults([]);
            setError('');
            return;
        }

        let isCanceled = false;
        setLoading(true);
        setError('');

        searchStores(term)
            .then((data) => {
                if (!isCanceled) {
                    setResults(Array.isArray(data) ? data : []);
                }
            })
            .catch((err) => {
                if (!isCanceled) {
                    setError(err?.message || '검색 중 오류가 발생했습니다.');
                }
            })
            .finally(() => {
                if (!isCanceled) setLoading(false);
            });

        return () => {
            isCanceled = true;
        };
    }, [keywordParam]);

    const handleSubmit = (event) => {
        event.preventDefault();
        const term = keyword.trim();
        if (!term) return;
        setSearchParams({ keyword: term });
    };

    const handleToggleFavorite = async (e, store) => {
        e.preventDefault();
        e.stopPropagation();

        const key = store.link || `${store.storeName}-${store.address}`;
        const existingId = favoriteIds.get(key);

        if (existingId) {
            // Delete
            try {
                await deleteFavorite(existingId);
                setFavoriteIds(prev => {
                    const next = new Map(prev);
                    next.delete(key);
                    return next;
                });
            } catch (err) {
                console.error("Failed to delete favorite", err);
                alert("즐겨찾기 삭제 실패");
            }
        } else {
            // Add
            try {
                const response = await addFavorite(store);
                // Response should contain the new ID
                const newId = response?.id || response?.favoriteId;
                if (newId) {
                    setFavoriteIds(prev => {
                        const next = new Map(prev);
                        next.set(key, newId);
                        return next;
                    });
                    alert('즐겨찾기에 추가되었습니다.');
                } else {
                    // Fallback reload if no ID returned
                    fetchFavorites();
                    alert('즐겨찾기에 추가되었습니다.');
                }
            } catch (err) {
                console.error("Failed to add favorite", err);
                alert("즐겨찾기 추가 실패: " + (err.message || "오류"));
            }
        }
    };

    const hasQuery = keywordParam.trim().length > 0;
    const statusMessage = !hasQuery
        ? '원하는 여행지를 검색해 보세요.'
        : loading
            ? '검색 중입니다...'
            : error
                ? error
                : results.length === 0
                    ? '검색 결과가 없습니다.'
                    : '';

    return (
        <div className={styles.page}>
            <button className={styles.backButton} onClick={() => navigate(-1)} aria-label="뒤로가기">
                <img src={backIcon} alt="" className={styles.backIcon} />
            </button>
            <form className={styles.searchBar} onSubmit={handleSubmit}>
                <button type="submit" className={styles.searchButton} aria-label="검색">
                    <img src={searchIcon} alt="" className={styles.searchIcon} />
                </button>
                <input
                    type="search"
                    className={styles.searchInput}
                    placeholder="여행지 검색하기"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                />
            </form>

            {statusMessage && (
                <p className={`${styles.helperText} ${error ? styles.errorText : ''}`}>{statusMessage}</p>
            )}

            {!error && !loading && results.length > 0 && (
                <div className={styles.results}>
                    {results.map((store, index) => {
                        const key = store.link || `${store.storeName}-${store.address}`;
                        const isFav = favoriteIds.has(key);

                        const CardContent = (
                            <>
                                <div className={styles.thumbnail}>
                                    {store.imageUrl ? (
                                        <img
                                            src={resolveMediaUrl(store.imageUrl)}
                                            alt={`${store.storeName} 이미지`}
                                            className={styles.thumbnailImage}
                                        />
                                    ) : (
                                        <div className={styles.thumbnailPlaceholder}>이미지 준비 중</div>
                                    )}
                                </div>
                                <div className={styles.cardBody}>
                                    <div className={styles.cardHeader}>
                                        <h3 className={styles.storeName}>{store.storeName}</h3>
                                        {store.rating && (
                                            <span className={styles.ratingBadge}>★ {store.rating}</span>
                                        )}
                                    </div>
                                    {store.category && <p className={styles.category}>{store.category}</p>}
                                    {store.address && <p className={styles.address}>{store.address}</p>}
                                    {store.reviewCount && (
                                        <p className={styles.reviewCount}>{store.reviewCount}개의 리뷰</p>
                                    )}
                                </div>
                                <button
                                    className={styles.favoriteButton}
                                    onClick={(e) => handleToggleFavorite(e, store)}
                                    aria-label={isFav ? "즐겨찾기 해제" : "즐겨찾기 추가"}
                                >
                                    <svg className={styles.favoriteIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M12 21l-1.45-1.32C6.4 15.36 3 12.28 3 8.5 3 6 5 4 7.5 4c1.54 0 3.04.99 3.57 2.36h1.86C13.46 4.99 14.96 4 16.5 4 19 4 21 6 21 8.5c0 3.78-3.4 6.86-7.55 11.18L12 21z"
                                            fill={isFav ? "#FF3C01" : "none"}
                                            stroke={isFav ? "#FF3C01" : "#DBDBDB"}
                                            strokeWidth="1.6"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            </>
                        );

                        return store.link ? (
                            <a
                                key={index}
                                className={styles.resultCard}
                                href={store.link}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {CardContent}
                            </a>
                        ) : (
                            <div key={index} className={styles.resultCard} role="article">
                                {CardContent}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Search;
